import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SecretariaService, MensagemSecretaria } from '../../../core/services/secretaria.service';
import { Subscription } from 'rxjs';
import { ChatUiService } from '../../../core/services/chat-ui.service';

@Component({
  selector: 'app-secretaria-chat',
  templateUrl: './secretaria-chat.component.html',
  styleUrls: ['./secretaria-chat.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class SecretariaChatComponent implements OnInit, OnDestroy {
  aberto = false;
  mensagens: MensagemSecretaria[] = [];
  texto = '';
  private selectedAssunto: string | null = null;
  private abertoSub?: Subscription;
  private closeAllSub?: Subscription;
  private typingTimeout?: any;
  private _aguardando = false;

  get aguardandoResposta(): boolean {
    return this._aguardando;
  }

  @ViewChild('scrollArea') scrollArea?: ElementRef<HTMLDivElement>;
  @ViewChild('inputMensagem') inputMensagem?: ElementRef<HTMLInputElement>;

  constructor(private svc: SecretariaService, private chatUi: ChatUiService) {}

  ngOnInit(): void {
    this.abertoSub = this.svc.isAberto$.subscribe(a => {
      this.aberto = a;
      if (a) {
        // Carrega mensagens existentes
        this.carregar();
        // Se não há nenhuma mensagem ainda, envia mensagem inicial com opções
        if (!this.mensagens || this.mensagens.length === 0) {
          this.svc.adicionarMensagemSecretariaComOpcoes('Como posso ajudar? Escolha um assunto:', ['Tarefas', 'Leads', 'Clientes']);
          this.carregar();
        }
        setTimeout(() => this.focusInput(), 0);
      } else {
        this.pararDigitando();
      }
    });
    this.closeAllSub = this.chatUi.closeAll$.subscribe(() => {
      this.svc.fechar();
      this.pararDigitando();
    });
  }

  ngOnDestroy(): void {
    if (this.abertoSub) this.abertoSub.unsubscribe();
    if (this.closeAllSub) this.closeAllSub.unsubscribe();
    this.pararDigitando();
  }

  toggle(): void {
    if (this.aberto) this.svc.fechar(); else this.svc.abrir();
  }

  isDireita(m: MensagemSecretaria): boolean {
    return m.autor === 'usuario';
  }

  private focusInput(): void {
    try {
      this.inputMensagem?.nativeElement?.focus();
    } catch {}
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const area = this.scrollArea?.nativeElement;
      if (area) area.scrollTop = area.scrollHeight;
    }, 0);
  }

  private carregar(): void {
    this.mensagens = this.svc.getMensagens();
    this.scrollToBottom();
  }

  enviar(): void {
    const conteudo = (this.texto || '').trim();
    if (!conteudo) return;

    const listaAntes = this.svc.getMensagens();

    this.svc.adicionarMensagemUsuario(conteudo);
    this.texto = '';

    // Se não houver nenhuma mensagem antes (primeiro contato), oferecer opções
    if (!listaAntes || listaAntes.length === 0) {
      this.svc.adicionarMensagemSecretariaComOpcoes('Assunto relacionado:', ['Tarefas', 'Leads', 'Clientes']);
    }

    this.carregar();

    // Simula indicador de digitação por curto período
    this._aguardando = true;
    this.scrollToBottom();
    this.pararDigitando(1200);
  }

  selecionarOpcao(m: MensagemSecretaria, opcao: string) {
    if (!opcao) return;
    const lower = (opcao || '').toLowerCase();
    const isAssunto = ['tarefas', 'leads', 'clientes'].includes(lower);
    const isAcao = ['criar', 'consultar', 'alterar'].includes(lower);

    // Sempre limpar as opções da mensagem clicada
    this.svc.limparOpcoes(m.id);

    // Se o usuário está escolhendo uma ação e já existe um assunto selecionado,
    // enviamos a mensagem combinada: "<Assunto> <acao>" (ex.: "Tarefas criar").
    if (isAcao && this.selectedAssunto) {
      const combinado = `${this.selectedAssunto} ${lower}`.trim();
      this.svc.adicionarMensagemUsuario(combinado);
      this.carregar();
      this.svc.registrarCliqueOpcao(m.id, opcao, m.texto);

      // Enviar para o backend exatamente o mesmo texto exibido na lista (mensagem combinada)
      this.svc.enviarMensagem(combinado).subscribe({
        next: () => {},
        error: () => {}
      });

      // Resetar estado para próxima interação
      this.selectedAssunto = null;

      // Breve indicador de digitação
      this._aguardando = true;
      this.scrollToBottom();
      this.pararDigitando(800);
      return;
    }

    // Caso seja seleção de assunto, guardamos o assunto e perguntamos pela ação.
    if (isAssunto) {
      this.selectedAssunto = opcao; // manter capitalização da opção
      this.svc.adicionarMensagemUsuario(opcao);
      this.carregar();
      this.svc.registrarCliqueOpcao(m.id, opcao, m.texto);

      // Enviar para o backend também o assunto selecionado
      this.svc.enviarMensagem(opcao).subscribe({
        next: () => {},
        error: () => {}
      });

      const singular = lower === 'tarefas' ? 'tarefa' : (lower === 'leads' ? 'lead' : 'cliente');
      const artigo = singular === 'tarefa' ? 'uma' : 'um';
      const pergunta = `Você deseja criar, consultar ou alterar ${artigo} ${singular}?`;
      this.svc.adicionarMensagemSecretariaComOpcoes(pergunta, ['Criar', 'Consultar', 'Alterar']);
      this.carregar();
      this._aguardando = false;
      return;
    }

    // Caso geral (nenhum dos casos acima): comportar-se como antes.
    this.svc.adicionarMensagemUsuario(opcao);
    this.carregar();
    this.svc.registrarCliqueOpcao(m.id, opcao, m.texto);

    // Enviar para o backend a opção selecionada
    this.svc.enviarMensagem(opcao).subscribe({
      next: () => {},
      error: () => {}
    });

    this._aguardando = true;
    this.scrollToBottom();
    this.pararDigitando(800);
  }

  private pararDigitando(delayMs?: number) {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = undefined;
    }
    if (delayMs && delayMs > 0) {
      this.typingTimeout = setTimeout(() => (this._aguardando = false), delayMs);
    } else {
      this._aguardando = false;
    }
  }
}
